import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { getCertificates } from '../api/certificates';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';

const certImages = [
  "/Excellence-in-Graphic-Design 6.png",
  "/Excellence-in-Graphic-Design 1.png",
  "/Excellence-in-Graphic-Design 5.png",
  "/Excellence-in-Graphic-Design 3.png",
  "/Excellence-in-Graphic-Design 2 (1).png",
  "/Excellence-in-Graphic-Design 2 (1).png",
];

const CertificationsSection = () => {
  const { t } = useTranslation('courses');
  const { isAr } = useLanguage();
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const autoScrollRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    getCertificates({ per_page: 50, search: '' })
      .then((json) => {
        console.log('Certificates API response:', json);
        const items = json.data || [];
        setCertifications(
          items.map((item, index) => ({
            id: item.id,
            name: item.holder?.name || '',
            achievement: isAr 
              ? (item.title_ar || item.course?.title_ar || item.course?.title || t('certifications.excellence'))
              : (item.title_en || item.course?.title_en || item.course?.title || t('certifications.excellence')),
            description: isAr 
              ? (item.description_ar || item.description || t('certifications.defaultDesc'))
              : (item.description_en || item.description || t('certifications.defaultDesc')),
            image: item.image_url || certImages[index % certImages.length],
            displayName: item.holder?.name || '',
            certificateNumber: item.certificate_number,
          }))
        );
      })
      .catch((err) => console.error('Failed to fetch certificates:', err))
      .finally(() => setLoading(false));
  }, [isAr, t]);

  // Auto-scroll تلقائي
  useEffect(() => {
    if (certifications.length === 0 || !scrollRef.current) return;

    const startScroll = setTimeout(() => {
      autoScrollRef.current = setInterval(() => {
        const container = scrollRef.current;
        if (!container) return;

        const rows = isMobile ? 1 : 2;
        const cols = Math.ceil(certifications.length / rows);
        const colWidth = container.scrollWidth / cols;
        const isAtEnd = Math.ceil(container.scrollLeft + container.clientWidth) >= container.scrollWidth - 2;

        if (isAtEnd) {
          container.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          container.scrollBy({ left: isAr ? -colWidth : colWidth, behavior: 'smooth' });
        }
      }, 3000);
    }, 500);

    return () => {
      clearTimeout(startScroll);
      clearInterval(autoScrollRef.current);
    };
  }, [certifications.length, isAr, isMobile]);

  return (
    <div className="w-full relative">
      {/* Title - Outside the background, with image behind text */}
      <div className="text-center mb-0 px-4 sm:px-6 lg:px-8 relative z-30">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
          <div style={{
            backgroundImage: 'url(/nzrzOfxWPwo-4.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'bott',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
            WebkitTextFillColor: 'transparent',
          }}>{t('certifications.title')}</div>
          <div className="text-black text-3xl sm:text-4xl lg:text-5xl font-medium mt-2">{t('certifications.subtitle')}</div>
        </h1>
      </div>

      {/* Medal - overlapping the background container */}
      <div className="flex justify-center relative z-40 mb-[-50px] sm:mb-[-60px] lg:mb-[-70px]">
        <img 
          src="/10603705_42828 2.png" 
          alt="Medal Icon" 
          className="w-24 h-24 sm:w-32 sm:h-32 lg:w-40 lg:h-40 object-contain"
        />
      </div>

      {/* Background section with content */}
      <section className="w-full min-h-screen relative py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 overflow-visible mb-16 sm:mb-20 lg:mb-24" style={{background: 'linear-gradient(135deg, rgba(230,230,230,0.3), rgba(255,255,255,0.3), rgba(219,219,219,0.3))', borderRadius: '120px'}}>
        
        {/* Background Image - Ellipse 64 */}
        <div className="absolute -top-80 right-0 w-3/4 h-full z-10 hidden sm:flex items-center">
          <img
            src="/Ellipse 64.png"
            alt="Background"
            className="w-full h-4/5 object-fill rounded-[70px]"
          />
        </div>
        
        <div className="relative z-20 max-w-7xl mx-auto">

        {/* Certifications Grid */}
          <style>{`.scrollbar-hide::-webkit-scrollbar{display:none}.scrollbar-hide{-ms-overflow-style:none;scrollbar-width:none}`}</style>

          {/* Mobile: Swiper */}
          <div className="block sm:hidden mb-8">
            <Swiper
              modules={[Autoplay]}
              slidesPerView={1}
              spaceBetween={16}
              loop={true}
              autoplay={{ delay: 3000, disableOnInteraction: false }}
              centeredSlides={true}
              className="w-full"
            >
              {loading ? null : certifications.map((cert, index) => (
                <SwiperSlide key={index}>
                  <div className="bg-white rounded-2xl p-4 border-4 border-gray-300 shadow-lg flex flex-col mx-2">
                    <div className="mb-3">
                      <h3 className="text-xs font-bold text-gray uppercase tracking-wider">{t('certifications.label')}</h3>
                      <h4 className="text-base font-normal text-black mb-1">{cert.name || t('certifications.student')}</h4>
                      <p className="text-sm text-gray-700">{cert.achievement}</p>
                    </div>
                    <p className="text-xs text-black mb-3 leading-relaxed">{cert.description}</p>
                    <div className="flex gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                      ))}
                    </div>
                    <div className="w-full aspect-[391/441]">
                      <img src={cert.image} alt={cert.name} className="w-full h-full object-contain"
                        onError={(e) => { e.target.src = certImages[index % certImages.length]; }} />
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Desktop: Grid */}
          <div
            ref={scrollRef}
            className="hidden sm:grid pb-4 scrollbar-hide mb-8 sm:mb-12"
            style={{
              gridTemplateRows: 'repeat(2, auto)',
              gridAutoFlow: 'column',
              gridAutoColumns: 'clamp(280px, calc((100% - 3rem) / 3), 400px)',
              gap: '1.5rem',
              overflowX: 'auto',
              overflowY: 'hidden',
            }}
          >
          {loading ? (
            <p className="text-gray-500 col-span-3 text-center">{t('certifications.loading')}</p>
          ) : (
          certifications.map((cert, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-5 md:p-3 lg:p-4 border-4 border-gray-300 shadow-lg overflow-visible flex flex-col"
            >
              {(index === 1 || index === 4) ? (
                <>
                  {/* Image Section - Top */}
                  <div className="relative mb-6 rounded-lg p-2 flex items-center justify-center flex-shrink-0">
                    <div className="relative rounded-lg flex items-center justify-center w-full aspect-[391/441]">
                      <img
                        src={cert.image}
                        alt={cert.name}
                        className="max-w-full max-h-full scale-110 object-contain"
                        onError={(e) => {
                          e.target.src = certImages[index % certImages.length];
                        }}
                      />
                    </div>
                  </div>

                  {/* Content - Bottom */}
                  <div className="mt-auto">
                    {/* Title */}
                    <div className="mb-4">
                      <div className="relative inline-flex items-center mb-2">
                        <h3 className="text-xs sm:text-lg lg:text-3xl font-bold text-gray uppercase tracking-wider">
                          {t('certifications.label')}
                        </h3>

                        <img
                          src="/10603705_42828 2.png"
                          alt="Decoration"
                          className="
                            absolute
                            top-1/2 -translate-y-1/2
                            w-20 h-20 sm:w-24 sm:h-24 lg:w-22 lg:h-22
                            object-contain
                            pointer-events-none
                          "
                          style={isAr ? { left: '-4.5rem' } : { right: '-4.5rem' }}
                        />
                      </div>

                      <h4 className="text-lg sm:text-xl lg:text-2xl font-normal text-black mb-1">
                        {cert.name || t('certifications.student')}
                      </h4>
                      <p className="text-sm sm:text-base text-gray-700 font-normal">
                        {cert.achievement}
                      </p>
                      {cert.certificateNumber && (
                        <p className="text-xs text-gray-400 mt-1">#{cert.certificateNumber}</p>
                      )}
                    </div>

                    {/* Description */}
                    <p className="text-xs sm:text-sm text-black mb-4 leading-relaxed">
                      {cert.description}
                    </p>

                    {/* Rating */}
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Title */}
                  <div className="mb-4">
                    <div className="relative inline-flex items-center mb-2">
                      <h3 className="text-xs sm:text-lg lg:text-2xl font-bold text-gray uppercase tracking-wider">
                        {t('certifications.label')}
                      </h3>

                      <img
                        src="/10603705_42828 2.png"
                        alt="Decoration"
                        className="
                          absolute
                          top-[100%] -translate-y-1/2
                          w-20 h-20 sm:w-24 sm:h-24 lg:w-22 lg:h-22
                          object-contain
                          pointer-events-none
                        "
                        style={isAr ? { left: '-4.5rem' } : { right: '-4.5rem' }}
                      />
                    </div>

                    <h4 className="text-lg sm:text-xl lg:text-2xl font-normal text-black mb-1">
                      {cert.name || t('certifications.student')}
                    </h4>
                    <p className="text-sm sm:text-base text-gray-700 font-normal">
                      {cert.achievement}
                    </p>
                    {cert.certificateNumber && (
                      <p className="text-xs text-gray-400 mt-1">#{cert.certificateNumber}</p>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-xs sm:text-sm text-black mb-4 leading-relaxed">
                    {cert.description}
                  </p>

                  {/* Rating */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    ))}
                  </div>

                  {/* Image Section - Bottom */}
                  <div className="relative mt-auto rounded-lg p-2 flex items-center justify-center">
                    <div className="relative rounded-lg flex items-center justify-center w-full aspect-[391/441]">
                      <img
                        src={cert.image}
                        alt={cert.name}
                        className="max-w-full max-h-full scale-110 object-contain"
                        onError={(e) => {
                          e.target.src = certImages[index % certImages.length];
                        }}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          ))
          )}
          </div>

        <div className="flex justify-center">
          <button
            className="btn-wave px-8 sm:px-10 lg:px-12 py-3 sm:py-4 rounded-full shadow-xl font-semibold text-sm sm:text-base lg:text-lg flex items-center border-2 border-gray-300 bg-gradient-to-r from-gray-500 via-white to-gray-300 text-black transition-transform duration-200"
          >
            <span>{t('certifications.viewAll')}</span>
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        </div>
      </section>
    </div>
  );
};

export default CertificationsSection;
